import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "@store/authStore";
import MainLayout from "@components/layouts/MainLayout";
import AuthLayout from "@components/layouts/AuthLayout";
import LoadingScreen from "@components/ui/LoadingScreen";

// ─── Lazy-loaded pages (code splitting) ──────────────────────────────────────
const LoginPage = lazy(() => import("@pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("@pages/auth/RegisterPage"));
const OAuthCallbackPage = lazy(() => import("@pages/auth/OAuthCallbackPage"));
const ForgotPasswordPage = lazy(() => import("@pages/auth/ForgotPasswordPage"));
const FeedPage = lazy(() => import("@pages/FeedPage"));
const LifeMapPage = lazy(() => import("@pages/LifeMapPage"));
const EventDetailPage = lazy(() => import("@pages/EventDetailPage"));
const AIAssistantPage = lazy(() => import("@pages/AIAssistantPage"));
const CalendarPage = lazy(() => import("@pages/CalendarPage"));
const DayDetailPage = lazy(() => import("@pages/DayDetailPage"));
const PostCreatePage = lazy(() => import("@pages/PostCreatePage"));
const ReelsPage = lazy(() => import("@pages/ReelsPage"));
const VideoPage = lazy(() => import("@pages/VideoPage"));
const ProfilePage = lazy(() => import("@pages/ProfilePage"));
const OtherProfilePage = lazy(() => import("@pages/OtherProfilePage"));
const SearchPage = lazy(() => import("@pages/SearchPage"));
const NotificationsPage = lazy(() => import("@pages/NotificationsPage"));
const AnalyticsPage = lazy(() => import("@pages/AnalyticsPage"));
const SettingsPage = lazy(() => import("@pages/SettingsPage"));
const ChallengesPage = lazy(() => import("@pages/ChallengesPage"));
const HighlightsPage = lazy(() => import("@pages/HighlightsPage"));
const JournalPage = lazy(() => import("@pages/JournalPage"));
const NotFoundPage = lazy(() => import("@pages/NotFoundPage"));
const PostDetailPage = lazy(() => import("@pages/PostDetailPage"));
const OnboardingPage = lazy(() => import("@pages/OnboardingPage"));
const OilaApp = lazy(() => import("./oila/OilaApp"));

// ─── Guard component ──────────────────────────────────────────────────────────
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return !isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
}

export default function App() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* ── Public / Auth routes ── */}
        <Route element={<AuthLayout />}>
          <Route
            path="/login"
            element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>}
          />
          <Route
            path="/register"
            element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>}
          />
          <Route
            path="/forgot-password"
            element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>}
          />
        </Route>

        {/* ── OAuth callback (public, handles token) ── */}
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />

        {/* ── Oila on lifegram.uz/oila/... (oila.lifegram.uz uses separate entry in main.tsx) ── */}
        <Route path="/oila/*" element={<OilaApp />} />

        {/* ── Authenticated routes ── */}
        <Route
          element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/lifemap" replace />} />
          <Route path="/feed" element={<FeedPage />} />
          <Route path="/lifemap" element={<LifeMapPage />} />
          <Route path="/reels" element={<ReelsPage />} />
          <Route path="/ai" element={<AIAssistantPage />} />
          <Route path="/event/:id" element={<EventDetailPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/calendar/:year" element={<CalendarPage />} />
          <Route path="/day/:date" element={<DayDetailPage />} />
          <Route path="/new" element={<PostCreatePage />} />
          <Route path="/video" element={<VideoPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/challenges" element={<ChallengesPage />} />
          <Route path="/highlights" element={<HighlightsPage />} />
          <Route path="/journal" element={<JournalPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/@:username" element={<OtherProfilePage />} />
          <Route path="/post/:id" element={<PostDetailPage />} />
          <Route path="/galaxy" element={<Navigate to="/lifemap?mode=galaxy" replace />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
